import { isCorpTenant, useIsTravelApp } from "@capone/common";
import { Box, useScrollTrigger } from "@material-ui/core";
import clsx from "clsx";
import {
  BackButton,
  BackToTopButton,
  FloatingMenuPill,
  IconName,
  isSpendAndGetOffer,
} from "halifax";
import React, { useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { SelectedTravelOfferScreen } from "redmond";
import { config } from "../../../../api/config";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_LIST_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { PackagesShopProgressBar } from "../../../book/components";
import { RewardsAccountSelection } from "../../../rewards/components";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";
import { PackagesAvailabilityCallState } from "../../reducer/state";
import { AvailabilityFilter } from "../AvailabilityFilter";
import { AvailabilityList } from "../AvailabilityList";
import { AvailabilityMap } from "../AvailabilityMap";
import { AvailabilityNoResults } from "../AvailabilityNoResults";
import { MobileAvailabilityMapRollingGallery } from "../MobileAvailabilityMapRollingGallery";
import { MobileLocationSelection } from "../MobileLocationSelection";
import { MobilePackagesHotelAvailabilityConnectorProps } from "./container";
import "./styles.scss";
import { parseQueryString } from "../../../search/utils/parseQueryString";

export interface IMobilePackagesHotelAvailabilityProps
  extends MobilePackagesHotelAvailabilityConnectorProps,
    RouteComponentProps {
  onShowMap?: (open: boolean) => void;
}

export const MobilePackagesHotelAvailability = (
  props: IMobilePackagesHotelAvailabilityProps
) => {
  const {
    lodgings,
    isFilteredHotelAvailabilityLodgingsEmpty,
    lodgingIdInFocus,
    hotelAvailabilityCallState,
    appliedFilterCount,
    onShowMap,
    history,
  } = props;

  const [showMap, setShowMap] = React.useState(false);
  const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false);

  const { isTravelApp } = useIsTravelApp();

  const expState = useExperiments();

  const packagesListEnhancementsEnabled =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_LIST_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const initialCallInProgress =
    hotelAvailabilityCallState ===
    PackagesAvailabilityCallState.InitialSearchCallInProcess;

  const bestOffer = useMemo(() => {
    const offer = lodgings.find((lodging) => !!lodging.bestOfferThisLodging)
      ?.bestOfferThisLodging;
    if (offer && isSpendAndGetOffer(offer.descriptions)) return undefined;
    return offer;
  }, [lodgings]);

  const parsedQueryString = useMemo(
    () => parseQueryString(history),
    [history.location.search]
  );

  useEffect(() => {
    if (parsedQueryString?.isTravelApp) {
      if (parsedQueryString?.showMap === undefined) {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set("showMap", showMap.toString());

        history.replace({
          ...history.location,
          search: searchParams.toString(),
        });
      } else if (
        parsedQueryString?.showMap !== undefined &&
        parsedQueryString?.showMap !== showMap
      ) {
        setShowMap(!!parsedQueryString?.showMap);
      }
    }
  }, [showMap, parsedQueryString?.showMap, !parsedQueryString?.isTravelApp]);

  useEffect(() => {
    onShowMap?.(showMap);
    if (showMap) {
      // removes medallia from the fixed bottom that was covering pricing (only on the enlarged map)
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "none";
      }
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [showMap]);

  const renderMap = (isPreview: boolean, onClickPreview?: () => void) => (
    <AvailabilityMap
      isPreview={isPreview}
      onClick={onClickPreview}
      onlyShowFilteredLodgings={packagesListEnhancementsEnabled}
    />
  );

  const renderFullHeightMap = () => (
    <Box className="mobile-lodging-availability-map-wrapper">
      <Box
        className={clsx("mobile-lodging-availability-map-container", {
          "full-height": lodgingIdInFocus === null,
        })}
      >
        {renderMap(false)}
        {!parsedQueryString?.isTravelApp && (
          <Box className="map-back-button-container">
            <BackButton onClick={() => setShowMap(false)} />
          </Box>
        )}
        <Box className="map-filters-button-container">
          <AvailabilityFilter
            renderOpenModalButton
            showMealPlanTypeFilter={packagesListEnhancementsEnabled}
            showDiscountFilter={packagesListEnhancementsEnabled}
            useHotelNameAutocomplete={packagesListEnhancementsEnabled}
            trackingLocation={
              packagesListEnhancementsEnabled ? "map" : undefined
            }
          />
        </Box>
      </Box>
      <Box className="mobile-lodging-availability-map-bottom-items">
        <Box className="mobile-lodging-availability-map-rolling-gallery">
          <MobileAvailabilityMapRollingGallery />
        </Box>
      </Box>
    </Box>
  );

  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });

  const renderLocationSearch = () => (
    <Box
      className={clsx("mobile-location-search-contents global-mobile-nav", {
        scrolled: scrollTrigger,
      })}
    >
      <MobileLocationSelection />
    </Box>
  );

  const renderRewardsAccount = () => (
    <Box className="mobile-rewards-account-contents global-mobile-nav">
      <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
    </Box>
  );

  const renderHotelListView = () => (
    <Box className="mobile-lodging-availability-contents">
      {!isFilteredHotelAvailabilityLodgingsEmpty && !initialCallInProgress && (
        <Box className="mobile-availability-list-wrapper">
          <AvailabilityList />
        </Box>
      )}
      {!initialCallInProgress && (
        <FloatingMenuPill
          items={[
            {
              label: "Map",
              onClick: () => {
                if (parsedQueryString?.isTravelApp) {
                  const searchParams = new URLSearchParams(
                    history.location.search
                  );
                  searchParams.set("showMap", "true");
                  history.push({
                    pathname: history.location.pathname,
                    search: searchParams.toString(),
                  });
                }
                setShowMap(true);
              },
              icon: IconName.MapOutline,
            },
            {
              label: "Sort & filter",
              onClick: () => setFilterModalOpen(true),
              icon: IconName.Settings,
              badge: appliedFilterCount
                ? String(appliedFilterCount)
                : undefined,
            },
          ]}
        />
      )}
    </Box>
  );

  return (
    <Box
      className={clsx(
        "mobile-package-lodging-availability-page",
        config.TENANT,
        { "travel-app": isTravelApp }
      )}
    >
      {!showMap && (
        <>
          {renderLocationSearch()}
          <PackagesShopProgressBar isMobile />
          {((isCorpTenant(config.TENANT) && bestOffer) ||
            !isCorpTenant(config.TENANT)) && (
            <TravelWalletDetailsBanner
              offer={isCorpTenant(config.TENANT) ? bestOffer : undefined}
              className="pkg-hotel-shop-offer"
              screen={SelectedTravelOfferScreen.PACKAGES_AVAILABILITY}
              showButton={false}
              bannerTextType="shop"
            />
          )}
          {renderRewardsAccount()}
          {!isFilteredHotelAvailabilityLodgingsEmpty && renderHotelListView()}

          <BackToTopButton />
        </>
      )}
      {showMap && renderFullHeightMap()}
      {isFilteredHotelAvailabilityLodgingsEmpty && (
        <Box className="mobile-availability-no-results-wrapper">
          <AvailabilityNoResults />
        </Box>
      )}
      <AvailabilityFilter
        renderOpenModalButton={false}
        filterModalOpen={filterModalOpen}
        setFilterModalOpen={setFilterModalOpen}
        showMealPlanTypeFilter={packagesListEnhancementsEnabled}
        showDiscountFilter={packagesListEnhancementsEnabled}
        useHotelNameAutocomplete={packagesListEnhancementsEnabled}
        trackingLocation={packagesListEnhancementsEnabled ? "list" : undefined}
      />
    </Box>
  );
};
